import { FormCheckbox, FormInput, FormPhoneInput, FormSelect } from "@/components";
import { FormProvider, UseFormReturn } from "react-hook-form";
import { EDIT_OWNER_FORM_ID } from "./useEditOwner";

interface EditOwnerProps {
  form: UseFormReturn<any>;
  onSubmit: (data: any) => void;
  stateOptions: { label: string; value: string }[];
  /** "create" (Add Owner): email optional, no secondary email, IDs generated on save. */
  mode?: "edit" | "create";
}

const SectionTitle = ({ children }: { children: string }) => (
  <h4 className="text-xs font-semibold uppercase tracking-wide text-primary">{children}</h4>
);

const AddressFields = ({ form, prefix, stateOptions }: EditOwnerProps & { prefix: string }) => {
  const errors: any = form.formState.errors?.[prefix] || {};
  const field = (name: string) => `${prefix}.${name}`;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <FormInput name={field("addressLine1")} control={form.control} label="Address Line 1"
        placeholder="Please enter" isRequired error={errors.addressLine1?.message} />
      <FormInput name={field("addressLine2")} control={form.control} label="Address Line 2"
        placeholder="Please enter" error={errors.addressLine2?.message} />
      <FormInput name={field("pobox")} control={form.control} label="PO Box"
        placeholder="Please enter" error={errors.pobox?.message} />
      <FormInput name={field("city")} control={form.control} label="City"
        placeholder="Please enter" isRequired error={errors.city?.message} variant="char" />
      <FormSelect name={field("state")} control={form.control} label="State"
        placeholder="Please select" isRequired error={errors.state?.message} options={stateOptions} />
      <FormInput name={field("zipCode")} control={form.control} label="ZIP Code"
        placeholder="XXXXX or XXXXX-XXXX" isRequired error={errors.zipCode?.message}
        maxLength={10} variant="integerSpeChar" />
      <FormInput name={field("country")} control={form.control} label="Country"
        placeholder="USA" error={errors.country?.message} disabled />
    </div>
  );
};

const EditOwner = ({ form, onSubmit, stateOptions, mode = "edit" }: EditOwnerProps) => {
  const isCreate = mode === "create";
  const errors: any = form.formState.errors;
  const separateCorrespondence = form.watch("separateCorrespondence");

  return (
    <FormProvider {...form}>
      <form id={EDIT_OWNER_FORM_ID} onSubmit={form.handleSubmit(onSubmit)}
        className="border rounded-lg p-4 flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <SectionTitle>Identity and Contact</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <FormInput name="title" control={form.control} label="Title"
              placeholder="Please enter" maxLength={5} error={errors.title?.message} />
            <FormInput name="firstName" control={form.control} label="First Name"
              placeholder="Please enter" isRequired error={errors.firstName?.message} />
            <FormInput name="lastName" control={form.control} label="Last Name"
              placeholder="Please enter" error={errors.lastName?.message} />
            <FormInput name="email" control={form.control} label="Primary Email"
              placeholder="Please enter" error={errors.email?.message} />
            {isCreate ? <></> : (
              <FormInput name="email2" control={form.control} label="Secondary Email"
                placeholder="Please enter" error={errors.email2?.message} />
            )}
            <FormPhoneInput name="phoneNumber" control={form.control} label="Mobile Number"
              placeholder="Please enter" isRequired error={errors.phoneNumber?.message} />
            <FormPhoneInput name="landlineNumber" control={form.control} label="Landline"
              placeholder="Please enter" error={errors.landlineNumber?.message} />
          </div>
          <p className="text-xs text-gray-500">
            {isCreate
              ? "User ID and Login ID are generated automatically when the owner is saved. No login credentials are sent."
              : "User ID, User Name, and Login ID are protected identifiers and are not changed here."}
          </p>
        </div>

        <div className="flex flex-col gap-4 border-t pt-4">
          <SectionTitle>Primary Contact Address</SectionTitle>
          <AddressFields form={form} onSubmit={onSubmit} stateOptions={stateOptions} prefix="address" />
        </div>

        <div className="flex flex-col gap-4 border-t pt-4">
          <SectionTitle>Correspondence Address</SectionTitle>
          <FormCheckbox name="separateCorrespondence" control={form.control}
            label="Correspondence address is different from the primary contact address" />
          {separateCorrespondence ? (
            <AddressFields form={form} onSubmit={onSubmit} stateOptions={stateOptions} prefix="correspondenceAddress" />
          ) : (
            <p className="text-sm text-gray-500">Same as primary contact address.</p>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default EditOwner;
